import { sqliteTable, text, integer, index } from "drizzle-orm/sqlite-core";

// ============================================================================
// conversations — การสนทนากับลูกค้าผ่านช่องทางการสื่อสาร
//
// Capability-neutral: references `connector_instance_id` + `channel_type`
// instead of a channel-specific page. The connectors module owns the
// provider credentials + webhook plumbing; chat just holds the conversation
// state keyed by (connectorInstanceId, externalUserId).
//
// NOTE: no Drizzle FK on `connectorInstanceId` — the connectors tables
// currently live in PLATFORM_DB (stopgap until #245), and cross-DB FKs
// are impossible in D1. Integrity is enforced via the
// `subscribePersisted` ownership check + the dispatcher.
// ============================================================================
export const conversations = sqliteTable(
  "chat_conversations",
  {
    id: text("id").primaryKey(), // ULID
    companyId: text("company_id").notNull(),
    /** Connector instance id — the channel this conversation lives on. */
    connectorInstanceId: text("connector_instance_id").notNull(),
    /** Capability-neutral channel type — `facebook-messenger | line-oa | …`. */
    channelType: text("channel_type").notNull().default("facebook-messenger"),
    /**
     * Provider-assigned user id for the customer on this channel.
     * - Facebook Messenger: PSID
     * - Line OA: line user id
     * - Instagram DM: IG user id
     */
    externalUserId: text("external_user_id").notNull(),
    senderName: text("sender_name"),
    senderAvatar: text("sender_avatar"),
    status: text("status").notNull().default("bot"),
    assignedTo: text("assigned_to"),
    lastMessageAt: text("last_message_at"),
    lastMessagePreview: text("last_message_preview"),
    unreadCount: integer("unread_count").notNull().default(0),
    aiAgentId: text("ai_agent_id"), // FK to ai_agents.id — per-conversation agent override
    metadata: text("metadata"), // JSON
    // Timestamps
    createdAt: text("created_at").notNull(),
    createdBy: text("created_by"),
    updatedAt: text("updated_at").notNull(),
    updatedBy: text("updated_by"),
    deletedAt: text("deleted_at"),
    deletedBy: text("deleted_by"),
  },
  (table) => ({
    companyStatusIdx: index("chat_conversations_company_status_idx").on(
      table.companyId,
      table.status
    ),
    /** Hot lookup path for `findOrCreateConversation` — every inbound webhook hits this. */
    connectorUserIdx: index("chat_conversations_connector_user_idx").on(
      table.connectorInstanceId,
      table.externalUserId
    ),
  })
);

export type Conversation = typeof conversations.$inferSelect;
export type NewConversation = typeof conversations.$inferInsert;
